import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { oesApi, adminApi, settingsApi, assessmentApi, integrationsApi } from '../../services/api';
import { getCriticalityLabels, getCriticalityRowColors } from '../../utils/settingsScales';
import CustomSelect from '../../components/common/CustomSelect';
import Toast from '../../components/Toast';
import ConfirmModal from '../../components/ConfirmModal';
import AssessmentGapModal from '../../components/admin/AssessmentGapModal';
import { getCountryFlag } from '../../utils/countryFlag';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';

const SECTOR_LABELS = {
  'energy': 'Energy',
  'telecom': 'Telecommunications',
  'finance': 'Finance',
  'health': 'Healthcare',
  'transport': 'Transportation',
  'water': 'Water',
  'government': 'Government',
  'other': 'Other',
};

export default function OESDetail() {
  const labels = useLabels();
  const t = useT();
  const { oesId } = useParams();
  const navigate = useNavigate();
  const [oes, setOes] = useState(null);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [sectorLists, setSectorLists] = useState([]);
  const [showGapModal, setShowGapModal] = useState(false);
  const [oesAssessment, setOesAssessment] = useState(null);

  // Integration / Security Evidence
  const [evidenceSummary, setEvidenceSummary] = useState(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  const [evidenceFindings, setEvidenceFindings] = useState([]);

  const role = localStorage.getItem('role');
  const isGlobalAdmin = role === 'global_admin';
  const isOrgAdmin = role === 'admin';
  const canManageOES = isGlobalAdmin || isOrgAdmin;

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  const [scales, setScales] = useState(null);

  useEffect(() => {
    fetchOESData();
    loadEvidenceSummary();
  }, [oesId]);

  const loadEvidenceSummary = async () => {
    try {
      const res = await integrationsApi.getOESSummary(oesId);
      if (res.data && res.data.total > 0) {
        setEvidenceSummary(res.data);
      } else if (res.data && res.data.integration_name) {
        setEvidenceSummary(res.data);
      }
    } catch { /* no integration data */ }
  };

  const handleSyncNow = async () => {
    setSyncLoading(true);
    try {
      // Find the integration for this OES
      const intRes = await integrationsApi.getAll();
      const integrations = intRes.data || [];
      const mapped = integrations.find(i => i.is_enabled && (i.oes_mappings || []).some(m => m.oes_id === parseInt(oesId)));
      if (!mapped) {
        showToast('error', `No integration configured for this ${labels.entityName}`);
        return;
      }
      await integrationsApi.syncOES(mapped.id, oesId);
      showToast('success', 'Sync completed');
      await loadEvidenceSummary();
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncLoading(false);
    }
  };

  const loadEvidenceFindings = async () => {
    try {
      const res = await integrationsApi.getOESFindings(oesId, { limit: 20 });
      setEvidenceFindings(res.data || []);
    } catch { setEvidenceFindings([]); }
  };

  useEffect(() => {
    settingsApi.getForCurrentUser()
      .then((r) => setScales(r.data?.scales ?? {}))
      .catch(() => setScales({}));
    // Load sector lists
    adminApi.getSectorLists()
      .then((r) => setSectorLists(r.data || []))
      .catch(() => setSectorLists([]));
  }, []);

  const criticalityLabels = getCriticalityLabels(scales);
  const criticalityRowColors = getCriticalityRowColors(scales);

  // Helper function to get sector name from IDs
  const getSectorName = (sectorListId, sectorId) => {
    if (!sectorListId || !sectorId) return 'Unassigned';
    const list = sectorLists.find(sl => sl.id === sectorListId);
    if (!list || !list.sectors) return 'Unknown';
    const sector = list.sectors.find(s => s.id === sectorId);
    return sector ? sector.name : 'Unknown';
  };

  const fetchOESData = async () => {
    try {
      setLoading(true);
      const [oesRes, usersRes] = await Promise.all([
        oesApi.getById(oesId),
        oesApi.getUsers(oesId)
      ]);
      
      setOes(oesRes.data);
      setUsers(usersRes.data);
      // Fetch the OES's assessment to get framework_id for posture assessment
      if (oesRes.data?.assessment_id) {
        try {
          const assessRes = await assessmentApi.getById(oesRes.data.assessment_id);
          setOesAssessment(assessRes.data);
        } catch { /* assessment may not be accessible */ }
      }
    } catch (error) {
      console.error('Error fetching OES data:', error);
      showToast('error', `Failed to load ${labels.entityName} details`);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getAllUsers(0, 1000);
      // Filter to only show regular users (role='user') that are not already assigned to this OES
      const assignedUserIds = new Set(users.map(u => u.id));
      const availableUsers = response.data.filter(
        user => user.role === 'user' && !assignedUserIds.has(user.id)
      );
      setAllUsers(availableUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast('error', 'Failed to load users');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;

    try {
      await oesApi.assignUser(oesId, selectedUserId);
      showToast('success', `User assigned to ${labels.entityName} successfully`);
      setShowAddUserModal(false);
      setSelectedUserId('');
      fetchOESData();
    } catch (error) {
      console.error('Error assigning user:', error);
      const errorMessage = error.response?.data?.detail || `Failed to assign user to ${labels.entityName}`;
      showToast('error', errorMessage);
    }
  };

  const handleRemoveUser = (user) => {
    setUserToRemove(user);
    setConfirmOpen(true);
  };

  const confirmRemoveUser = async () => {
    if (!userToRemove) return;
    
    try {
      await oesApi.removeUser(oesId, userToRemove.id);
      showToast('success', `User removed from ${labels.entityName} successfully`);
      fetchOESData();
    } catch (error) {
      console.error('Error removing user:', error);
      const errorMessage = error.response?.data?.detail || `Failed to remove user from ${labels.entityName}`;
      showToast('error', errorMessage);
    } finally {
      setConfirmOpen(false);
      setUserToRemove(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-eucnet-blue"></div>
        </div>
      </Layout>
    );
  }

  if (!oes) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <h2 className="text-xl font-bold text-eucnet-navy mb-2">{t('entity.notFound', { entity: labels.entityName })}</h2>
            <p className="text-eucnet-grey-600 mb-4">{t('entity.notFoundDesc', { entity: labels.entityName })}</p>
            <button
              onClick={() => navigate('/oes')}
              className="bg-eucnet-blue hover:bg-eucnet-navy text-white px-4 py-2 rounded-lg"
            >
              {t('entity.backToManagement', { entity: labels.entityName })}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>

      <Toast
        open={toast.open}
        setOpen={(open) => setToast(prev => ({ ...prev, open }))}
        type={toast.type}
        className="fixed top-6 right-6 z-[60]"
      >
        {toast.message}
      </Toast>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/oes')}
          className="mb-4 text-eucnet-blue hover:text-eucnet-navy flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          {t('entity.backToManagement', { entity: labels.entityName })}
        </button>

        {/* OES Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-start gap-4">
              {oes.logo && (
                <img
                  src={oes.logo.startsWith('data:') ? oes.logo : `data:image/jpeg;base64,${oes.logo}`}
                  alt={`${oes.name} logo`}
                  className="h-16 w-auto max-w-full object-contain"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold text-eucnet-navy">{oes.name}</h1>
                {oes.short_name && (
                  <p className="mt-1 text-eucnet-grey-600">{oes.short_name}</p>
                )}
                {oes.description && (
                  <p className="mt-2 text-eucnet-grey-600">{oes.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/oes/${oesId}/profile`)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-eucnet-sky hover:bg-eucnet-navy rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                {t('entity.profile', { entity: labels.entityName })}
              </button>
              {oesAssessment?.framework_id && (
                <button
                  onClick={() => setShowGapModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-eucnet-navy border border-eucnet-navy/30 hover:bg-eucnet-navy hover:text-white rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  {t('assessment.postureAssessment')}
                </button>
              )}
              {oes.oes_criticality && (
                <div className={`px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide shadow-md ${criticalityRowColors[oes.oes_criticality.toLowerCase()] || 'bg-gray-200 text-gray-800'}`}>
                  {criticalityLabels[oes.oes_criticality.toLowerCase()] || oes.oes_criticality}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6 pt-6 border-t border-eucnet-grey-200">
            <div>
              <div className="text-sm text-eucnet-grey-600 mb-1">{t('entity.sector')}</div>
              <div className="text-lg font-semibold text-eucnet-navy">
                {getSectorName(oes.sector_list_id, oes.sector_id)}
              </div>
            </div>
            {oes.country && (
              <div>
                <div className="text-sm text-eucnet-grey-600 mb-1">{t('entity.country')}</div>
                <div className="text-lg font-semibold text-eucnet-navy flex items-center gap-2">
                  <span>{oes.country}</span>
                  <span className="text-3xl inline-block align-middle" aria-hidden="true">{getCountryFlag(oes.country)}</span>
                </div>
              </div>
            )}
            {oes.email && (
              <div>
                <div className="text-sm text-eucnet-grey-600 mb-1">{t('common.email')}</div>
                <div className="text-lg font-semibold text-eucnet-navy">
                  <a href={`mailto:${oes.email}`} className="text-eucnet-blue hover:underline">
                    {oes.email}
                  </a>
                </div>
              </div>
            )}
            {oes.website && (
              <div>
                <div className="text-sm text-eucnet-grey-600 mb-1">{t('entity.website')}</div>
                <div className="text-lg font-semibold text-eucnet-navy">
                  <a 
                    href={oes.website.startsWith('http') ? oes.website : `https://${oes.website}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-eucnet-blue hover:underline"
                  >
                    {oes.website}
                  </a>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Security Evidence Section */}
        {evidenceSummary && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-semibold text-eucnet-navy">{t('integrations.securityEvidence')}</h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {evidenceSummary.integration_type === 'crowdstrike_falcon' ? 'CrowdStrike Falcon' : evidenceSummary.integration_name || 'External Integration'}
                  {evidenceSummary.last_synced_at && (
                    <> &middot; Last synced: {new Date(evidenceSummary.last_synced_at).toLocaleString()}</>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={handleSyncNow}
                disabled={syncLoading}
                className="px-3 py-1.5 text-xs font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy transition-colors disabled:opacity-50"
              >
                {syncLoading ? t('integrations.syncing') : t('integrations.syncNow')}
              </button>
            </div>

            {/* Severity badges */}
            <div className="grid grid-cols-4 gap-3 mb-3">
              {[
                { key: 'critical', label: 'Critical', color: 'bg-red-500' },
                { key: 'high', label: 'High', color: 'bg-orange-500' },
                { key: 'medium', label: 'Medium', color: 'bg-amber-400' },
                { key: 'low', label: 'Low', color: 'bg-blue-400' },
              ].map(({ key, label, color }) => (
                <div key={key} className="text-center p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div className={`text-2xl font-bold ${key === 'critical' ? 'text-red-600' : key === 'high' ? 'text-orange-600' : key === 'medium' ? 'text-amber-600' : 'text-blue-600'}`}>
                    {evidenceSummary[key] || 0}
                  </div>
                  <div className="text-[10px] font-medium text-slate-500 uppercase mt-0.5">{label}</div>
                </div>
              ))}
            </div>

            {/* Discrepancy alert */}
            {(evidenceSummary.critical + evidenceSummary.high) > 10 && (
              <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2 mb-3">
                <svg className="w-4 h-4 mt-0 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                <span>
                  <strong>{evidenceSummary.critical + evidenceSummary.high}</strong> critical/high vulnerabilities detected.
                  {' '}Review posture assessment for potential discrepancies with self-reported compliance.
                </span>
              </div>
            )}

            {/* Expandable findings */}
            <button
              type="button"
              onClick={() => {
                if (!evidenceExpanded) loadEvidenceFindings();
                setEvidenceExpanded(!evidenceExpanded);
              }}
              className="text-xs text-eucnet-sky hover:text-eucnet-navy font-medium"
            >
              {evidenceExpanded ? 'Hide findings' : `View findings (${evidenceSummary.total} total)`}
            </button>
            {evidenceExpanded && evidenceFindings.length > 0 && (
              <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600">
                      <th className="px-3 py-2 text-left font-medium">CVE</th>
                      <th className="px-3 py-2 text-left font-medium">Severity</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                      <th className="px-3 py-2 text-left font-medium">Host</th>
                      <th className="px-3 py-2 text-left font-medium">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evidenceFindings.map(f => (
                      <tr key={f.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono text-slate-700">{f.title || f.source_id}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${
                            f.severity === 'CRITICAL' ? 'bg-red-500' : f.severity === 'HIGH' ? 'bg-orange-500' : f.severity === 'MEDIUM' ? 'bg-amber-400' : 'bg-blue-400'
                          }`}>{f.severity}</span>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{f.status}</td>
                        <td className="px-3 py-2 text-slate-600">{f.details?.hostname || '-'}</td>
                        <td className="px-3 py-2 text-slate-500">{f.last_seen_at ? new Date(f.last_seen_at).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Users Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-eucnet-navy">{t('entity.members', { entity: labels.entityName })}</h2>
            {canManageOES && (
              <button
                onClick={() => {
                  fetchUsers();
                  setShowAddUserModal(true);
                }}
                className="bg-eucnet-blue hover:bg-eucnet-navy text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                + {t('entity.addUser')}
              </button>
            )}
          </div>

          {users.length === 0 ? (
            <p className="text-eucnet-grey-600 text-center py-8">{t('entity.noUsersAssigned')}</p>
          ) : (
            <div className="space-y-2">
              {users.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 bg-eucnet-grey-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {user.user_info?.avatar && (
                      <img
                        src={user.user_info.avatar.startsWith('data:') ? user.user_info.avatar : `data:image/jpeg;base64,${user.user_info.avatar}`}
                        alt={`${user.user_info.first_name} ${user.user_info.last_name}`}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <div className="font-medium text-eucnet-navy">
                        {user.user_info?.first_name} {user.user_info?.last_name}
                      </div>
                      <div className="text-sm text-eucnet-grey-600">{user.email}</div>
                    </div>
                  </div>
                  {canManageOES && (
                    <button
                      onClick={() => handleRemoveUser(user)}
                      className="text-red-600 hover:text-red-800 p-2"
                      title={`Remove from ${labels.entityName}`}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                        <path d="M8 8l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-eucnet-navy mb-4">{t('entity.addUserTo', { entity: labels.entityName })}</h3>
            <form onSubmit={handleAddUser}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('entity.selectUser')}
                </label>
                <CustomSelect
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  options={[
                    { value: '', label: t('entity.selectAUser') },
                    ...allUsers.map(user => ({
                      value: String(user.id),
                      label: `${user.user_info?.first_name || ''} ${user.user_info?.last_name || ''} (${user.email})`.trim() || user.username
                    }))
                  ]}
                  placeholder={t('entity.selectAUser')}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setSelectedUserId('');
                  }}
                  className="px-4 py-2 border border-eucnet-grey-300 text-eucnet-grey-700 rounded-lg hover:bg-eucnet-grey-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={!selectedUserId}
                  className="px-4 py-2 bg-eucnet-blue text-white rounded-lg hover:bg-eucnet-navy disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('entity.addUser')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Remove Modal */}
      <ConfirmModal
        open={confirmOpen}
        title={t('entity.removeUser', { entity: labels.entityName })}
        message={userToRemove ? `Are you sure you want to remove "${userToRemove.user_info?.first_name || ''} ${userToRemove.user_info?.last_name || ''}" (${userToRemove.email}) from this ${labels.entityName}? This action cannot be undone.` : `Are you sure you want to remove this user from the ${labels.entityName}?`}
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
        onConfirm={confirmRemoveUser}
        onCancel={() => {
          setConfirmOpen(false);
          setUserToRemove(null);
        }}
      />

      {oesAssessment?.framework_id && (
        <AssessmentGapModal
          isOpen={showGapModal}
          onClose={() => setShowGapModal(false)}
          assessment={oesAssessment}
          oesId={parseInt(oesId)}
          oesName={oes?.name}
          onNotify={(type, msg) => showToast(type, msg)}
        />
      )}
    </Layout>
  );
}
